/**
 * Orders and Dispatch (docs/designs/dispatch-module.md, user 2026-10-30): the rules the pages and the
 * API share.
 *
 * SeedTrac runs this on two logins: Order Booking books Sales / Stock Transfer / TDF orders and
 * releases them, Dispatch allocates, scan-loads and prints. NexSeed folds it into one flow (user's
 * choice): Book → Approve → Dispatch. Approval replaces Release, and a dispatch draws on the
 * approved lines of its party as packs are scanned or lots are picked, so there is no separate
 * Allocation document.
 *
 *     order:    draft ──approve──▶ approved ──(dispatches)──▶ part dispatched ──▶ dispatched
 *                 └──cancel──▶ cancelled        └──short-close──▶ closed
 *     dispatch: draft ──preview──▶ previewed ──final submit──▶ submitted (loaded) ──gate pass──▶ out
 */
import { toDecimal } from "./decimal";
import type { DispatchTransport } from "./dispatch-stock-transfer";
import { missingDispatchTransport } from "./dispatch-stock-transfer";

/* ── Customer Master ─────────────────────────────────────────────────────────────────────── */

/** SeedTrac's party types for orders and dispatch (Order Release lists them all). */
export const CUSTOMER_TYPES = [
  "branch",
  "cnf",
  "dealer",
  "bulk",
  "export_buyer",
  "tdf_individual",
  "discard_buyer",
] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];
export const CUSTOMER_TYPE_LABELS: Record<CustomerType, string> = {
  branch: "Branch",
  cnf: "C&F",
  dealer: "Dealer",
  bulk: "Bulk",
  export_buyer: "Export Buyer",
  tdf_individual: "TDF - Individual",
  discard_buyer: "Discard Buyer",
};
/** SeedTrac's own label for each type, as its reports print it (the harvest maps these back). */
export const CUSTOMER_TYPE_FROM_SEEDTRAC: Record<string, CustomerType> = {
  Branch: "branch",
  "C&F": "cnf",
  Dealer: "dealer",
  Bulk: "bulk",
  "Export Buyer": "export_buyer",
  "TDF - Individual": "tdf_individual",
  "Discard Buyer": "discard_buyer",
};

/* ── Orders ──────────────────────────────────────────────────────────────────────────────── */

export const ORDER_TYPES = ["sales", "stock_transfer", "tdf"] as const;
export type OrderType = (typeof ORDER_TYPES)[number];

export const ORDER_TYPE_META: Record<
  OrderType,
  {
    label: string;
    /** The order number's prefix (SeedTrac's: OS779/26-27/OB2). */
    prefix: string;
    /** The note the order prints. */
    note: string;
    noteTitle: string;
    /** The party types an order of this type is booked for. */
    partyTypes: readonly CustomerType[];
  }
> = {
  sales: {
    label: "Sales",
    prefix: "OS",
    note: "SON",
    noteTitle: "Sales Order Note",
    partyTypes: ["dealer", "bulk", "export_buyer"],
  },
  stock_transfer: {
    label: "Stock Transfer",
    prefix: "OT",
    note: "TON",
    noteTitle: "Transfer Order Note",
    partyTypes: ["branch", "cnf"],
  },
  tdf: {
    label: "Trial / Demo / Free",
    prefix: "OD",
    note: "DON",
    noteTitle: "Demo Order Note",
    partyTypes: ["tdf_individual", "branch", "cnf", "dealer"],
  },
};

/** What is stored. Part Dispatched and Dispatched are read from the lines' balances. */
export const ORDER_STATUSES = [
  "draft",
  "approved",
  "closed",
  "cancelled",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** The list's tabs, in the order an order walks them. */
export const ORDER_TABS = [
  "draft",
  "open",
  "dispatched",
  "closed",
  "cancelled",
] as const;
export type OrderTab = (typeof ORDER_TABS)[number];
export const ORDER_TAB_LABELS: Record<OrderTab, string> = {
  draft: "Draft",
  open: "Approved",
  dispatched: "Dispatched",
  closed: "Short-closed",
  cancelled: "Cancelled",
};

/** Standard (pack layout) or non-standard pack: SeedTrac's UPS Type. Bulk has no pack. */
export const UPS_TYPES = ["ST", "NST", "BULK"] as const;
export type UpsType = (typeof UPS_TYPES)[number];
export const UPS_TYPE_LABELS: Record<UpsType, string> = {
  ST: "Standard",
  NST: "Non-Standard",
  BULK: "Bulk",
};

export type OrderProgress = "pending" | "part_dispatched" | "dispatched";

/** Where a line stands: nothing sent, some sent, all of it sent. */
export function lineProgress(
  qty: string | number,
  dispatched: string | number
): OrderProgress {
  const q = toDecimal(qty);
  const d = toDecimal(dispatched);
  if (d.lte(0)) return "pending";
  return d.gte(q) ? "dispatched" : "part_dispatched";
}

/** An order's progress from its lines: dispatched when every line is, pending when none moved. */
export function orderProgress(
  lines: readonly { qtyKg: string | number; dispatchedQty: string | number }[]
): OrderProgress {
  if (!lines.length) return "pending";
  const each = lines.map((l) => lineProgress(l.qtyKg, l.dispatchedQty));
  if (each.every((p) => p === "dispatched")) return "dispatched";
  if (each.every((p) => p === "pending")) return "pending";
  return "part_dispatched";
}

export const PROGRESS_LABELS: Record<OrderProgress, string> = {
  pending: "Pending",
  part_dispatched: "Part Dispatched",
  dispatched: "Dispatched",
};

/**
 * Pouches (NoP) and master packs (NoMP) for a quantity of one pack size, as SeedTrac's order line
 * derives them from the UPS master: NoP = kg / UPS, NoMP = NoP over the layout's pouches per MP,
 * rounded up (the last pack may be part-filled). Null where the size or layout is unknown.
 */
export function packCounts(
  qtyKg: string | number,
  upsGrams: string | number | null | undefined,
  layout?: { pouchesInWb: number; wbInMp: number } | null
): { nop: number | null; nowb: number | null; nomp: number | null } {
  const g = toDecimal(upsGrams ?? 0);
  if (g.lte(0)) return { nop: null, nowb: null, nomp: null };
  const nop = Math.round(toDecimal(qtyKg).mul(1000).div(g).toNumber());
  if (!layout || layout.pouchesInWb <= 0 || layout.wbInMp <= 0)
    return { nop, nowb: null, nomp: null };
  const nowb = Math.ceil(nop / layout.pouchesInWb);
  return { nop, nowb, nomp: Math.ceil(nowb / layout.wbInMp) };
}

/** What stops an order's Approve: a party and at least one line. */
export function orderBlockers(o: {
  customerId: number | null | undefined;
  lineCount: number;
}): string[] {
  const missing: string[] = [];
  if (!o.customerId) missing.push("Party Name");
  if (o.lineCount === 0) missing.push("at least one item");
  return missing;
}

/* ── Holding / Unholding ─────────────────────────────────────────────────────────────────── */

/** SeedTrac's Holding types: a variety, a party, or one order. */
export const HOLD_SCOPES = ["variety", "party", "order"] as const;
export type HoldScope = (typeof HOLD_SCOPES)[number];
export const HOLD_SCOPE_LABELS: Record<HoldScope, string> = {
  variety: "Variety",
  party: "Party",
  order: "Order",
};
/** Which orders a hold covers: commercial (Sales, Stock Transfer), TDF, or both. */
export const HOLD_CATEGORIES = ["commercial", "tdf", "both"] as const;
export type HoldCategory = (typeof HOLD_CATEGORIES)[number];
export const HOLD_CATEGORY_LABELS: Record<HoldCategory, string> = {
  commercial: "Commercial",
  tdf: "TDF",
  both: "Both",
};

/** Whether a hold of this category covers an order of this type. */
export const holdCovers = (category: HoldCategory, type: OrderType) =>
  category === "both" || (category === "tdf") === (type === "tdf");

/* ── Dispatch ────────────────────────────────────────────────────────────────────────────── */

export const DISPATCH_PACK_FORM = "dispatch_pack";
export const DISPATCH_BULK_FORM = "dispatch_bulk";
export const DISPATCH_TDF_FORM = "dispatch_tdf";
export const DISPATCH_DISCARD_FORM = "dispatch_discard";

export const SALES_DISPATCH_FORMS = [
  DISPATCH_PACK_FORM,
  DISPATCH_BULK_FORM,
  DISPATCH_TDF_FORM,
  DISPATCH_DISCARD_FORM,
] as const;
export type SalesDispatchForm = (typeof SALES_DISPATCH_FORMS)[number];

export const isSalesDispatchForm = (f: string): f is SalesDispatchForm =>
  (SALES_DISPATCH_FORMS as readonly string[]).includes(f);

/** The dispatch kinds as the URL names them. */
export const DISPATCH_KINDS = ["pack", "bulk", "tdf", "discard"] as const;
export type DispatchKind = (typeof DISPATCH_KINDS)[number];

export const DISPATCH_KIND_META: Record<
  DispatchKind,
  {
    form: SalesDispatchForm;
    title: string;
    /** The transaction number's prefix (SeedTrac's PD25153/Y/DP7, BD3567, TD…, MD24). */
    prefix: string;
    /** The dispatch note's code and title. */
    note: string;
    noteTitle: string;
    /** How stock is put on it: master-pack barcodes, or lots by bags and quantity. */
    loading: "scan" | "lots";
    /** The order types it fulfils; none for a discard. */
    orderTypes: readonly OrderType[];
    /** The party types it may go to; null takes a typed party (Material Discard). */
    partyTypes: readonly CustomerType[] | null;
    hint: string;
  }
> = {
  pack: {
    form: DISPATCH_PACK_FORM,
    title: "Pack Seed Dispatch",
    prefix: "PD",
    note: "STDN",
    noteTitle: "Stock Transfer Dispatch Note",
    loading: "scan",
    orderTypes: ["stock_transfer", "sales"],
    partyTypes: ["branch", "cnf", "dealer", "export_buyer"],
    hint: "Scan master packs onto the truck against approved orders",
  },
  bulk: {
    form: DISPATCH_BULK_FORM,
    title: "Bulk Seed Dispatch",
    prefix: "BD",
    note: "BSDN",
    noteTitle: "Bulk Seed Dispatch Note",
    loading: "lots",
    orderTypes: ["sales", "stock_transfer"],
    partyTypes: ["bulk", "branch", "cnf", "dealer", "export_buyer"],
    hint: "Conditioned seed lots by bags and quantity",
  },
  tdf: {
    form: DISPATCH_TDF_FORM,
    title: "TDF Seed Dispatch",
    prefix: "TD",
    note: "TDFSDN",
    noteTitle: "TDF Seed Dispatch Note",
    loading: "lots",
    orderTypes: ["tdf"],
    partyTypes: ["tdf_individual", "branch", "cnf", "dealer"],
    hint: "Trial, demo and free seed against TDF orders",
  },
  discard: {
    form: DISPATCH_DISCARD_FORM,
    title: "Material Discard",
    prefix: "MD",
    note: "MDN",
    noteTitle: "Material Discard Note",
    loading: "lots",
    orderTypes: [],
    partyTypes: null,
    hint: "Lots sent out to be discarded, on a discard instruction",
  },
};

export const dispatchKindOfForm = (form: string): DispatchKind | null =>
  DISPATCH_KINDS.find((k) => DISPATCH_KIND_META[k].form === form) ?? null;

/** A Pack Seed dispatch for a branch or C&F prints STDN; for anyone else the note is a DN. */
export const packNoteTitle = (type: CustomerType | null | undefined) =>
  type === "branch" || type === "cnf"
    ? "Stock Transfer Dispatch Note (STDN)"
    : "Dispatch Note (DN)";

/** The list's tabs: the paperwork, then the truck. */
export const DISPATCH_TABS = [
  "draft",
  "previewed",
  "loaded",
  "dispatched",
  "cancelled",
] as const;
export type DispatchTab = (typeof DISPATCH_TABS)[number];
export const DISPATCH_TAB_LABELS: Record<DispatchTab, string> = {
  draft: "Pending",
  previewed: "Preview",
  loaded: "Loaded",
  dispatched: "Dispatched",
  cancelled: "Cancelled",
};

/** Material Discard's Return Status. */
export const RETURN_STATUSES = ["returnable", "not_returnable"] as const;
export type ReturnStatus = (typeof RETURN_STATUSES)[number];

/** A discard's typed party (SeedTrac's Material Discard header). */
export interface DiscardParty {
  name?: string | null | undefined;
  address?: string | null | undefined;
  city?: string | null | undefined;
  state?: string | null | undefined;
  pincode?: string | null | undefined;
  phone?: string | null | undefined;
}

/** What stops a dispatch's Preview: the party, the carriage, and something loaded. */
export function salesDispatchBlockers(d: {
  kind: DispatchKind;
  customerId: number | null | undefined;
  party?: DiscardParty | null | undefined;
  instructionRef?: string | null | undefined;
  transitApplicable?: boolean | null | undefined;
  transport: DispatchTransport | null | undefined;
  lineCount: number;
}): string[] {
  const missing: string[] = [];
  if (d.kind === "discard") {
    if (!(d.instructionRef ?? "").trim())
      missing.push("Discard Instruction Ref. No.");
    if (!(d.party?.name ?? "").trim()) missing.push("Party Name");
  } else if (!d.customerId) missing.push("Party Name");
  if (d.kind !== "discard" || d.transitApplicable !== false)
    missing.push(...missingDispatchTransport(d.transport ?? {}));
  if (d.lineCount === 0)
    missing.push(
      DISPATCH_KIND_META[d.kind].loading === "scan"
        ? "at least one master pack"
        : "at least one lot"
    );
  return missing;
}

/** A master pack barcode: 2 letters and 9 digits, the packing floor's and OFL's alike. */
export const MASTER_PACK_BARCODE = /^[A-Z]{2}\d{9}$/;

/**
 * The barcodes From → To, both ends included: the same two letters and a numeric run (SeedTrac's
 * "Direct Dispatch with Range"). Null when the two do not make a range, or it is over `max` long.
 */
export function barcodeRange(
  from: string,
  to: string,
  max = 500
): string[] | null {
  const a = from.trim().toUpperCase();
  const b = to.trim().toUpperCase();
  if (!MASTER_PACK_BARCODE.test(a) || !MASTER_PACK_BARCODE.test(b)) return null;
  if (a.slice(0, 2) !== b.slice(0, 2)) return null;
  const lo = Number(a.slice(2));
  const hi = Number(b.slice(2));
  if (hi < lo || hi - lo + 1 > max) return null;
  const out: string[] = [];
  for (let n = lo; n <= hi; n++)
    out.push(`${a.slice(0, 2)}${String(n).padStart(9, "0")}`);
  return out;
}
